/**
 * The frame around one profile: header (name, age, my relation) + bottom
 * navigation. Pages read the overview through `useProfile()`.
 */

import { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useParams } from '@tanstack/react-router';
import { Api } from '@/core/api';
import type { ProfileOverview } from '@/core/types';
import { formatAge } from '@/core/jalali';

const ProfileCtx = createContext<ProfileOverview | null>(null);

export function useProfile(): ProfileOverview {
  const p = useContext(ProfileCtx);
  if (!p) throw new Error('useProfile outside ProfileShell');
  return p;
}

const ROLE_FA: Record<string, string> = {
  guardian: 'سرپرست',
  self: 'خودم',
  practitioner: 'درمانگر',
  reporter: 'گزارش‌دهنده',
  system: 'سامانه',
};

const icons = {
  today: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 3l9 7v11a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V10z" />
    </svg>
  ),
  map: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z" />
      <path d="M9 4v14M15 6v14" />
    </svg>
  ),
  timeline: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  share: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M17 8h5M19.5 5.5v5" />
    </svg>
  ),
};

export function ProfileShell() {
  const { id } = useParams({ from: '/authed/p/$id' });
  const q = useQuery({ queryKey: ['profile', id], queryFn: () => Api.overview(id) });
  if (q.isLoading)
    return (
      <div className="page">
        <div className="skeleton" />
      </div>
    );
  if (q.isError || !q.data)
    return (
      <div className="page">
        <div className="error">این پروفایل در دسترس شما نیست.</div>
        <Link to="/" className="btn quiet">
          بازگشت
        </Link>
      </div>
    );
  const p = q.data;
  return (
    <ProfileCtx.Provider value={p}>
      <header className="page" style={{ paddingBlockEnd: 0 }}>
        <div className="row spread">
          <div>
            <div className="overline">
              {ROLE_FA[p.myRole] ?? p.myRole}
              {p.myBranches ? ` · ${p.myBranches.length} شاخه` : ''}
            </div>
            <h1 className="page-title">{p.displayName}</h1>
            <div className="help">{formatAge(p.ageMonths)}</div>
          </div>
          <Link to="/" className="btn quiet" aria-label="همهٔ پروفایل‌ها">
            ⌂
          </Link>
        </div>
      </header>
      <Outlet />
      <nav className="nav" aria-label="بخش‌ها">
        <div className="nav-inner">
          <Link to="/p/$id" params={{ id }} activeOptions={{ exact: true }}>
            {icons.today}
            امروز
          </Link>
          <Link to="/p/$id/map" params={{ id }}>
            {icons.map}
            نقشه
          </Link>
          <Link to="/p/$id/timeline" params={{ id }}>
            {icons.timeline}
            خط زمان
          </Link>
          <Link to="/p/$id/share" params={{ id }}>
            {icons.share}
            دسترسی
          </Link>
        </div>
      </nav>
    </ProfileCtx.Provider>
  );
}
